// ==========================
// Version 3 — pages/AdminFlags.js
// - Toast-based feedback (no alert())
// - Adds "Open" link to memory
// - Shows resolution note on resolved flags
// - Keeps composite-index safety message
// ==========================
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { queryFlags, resolveFlag } from '../db';
import { toast } from '../utils/toast';

const NAVY = '#0A1A33';
const IVORY = '#FDFCF9';
const GOLD = '#C9A44C';
const WARM_GRAY = '#888888';
const ACCESSIBLE_GOLD = '#8A6D2E';
const BURGUNDY = '#5C1A2B';
const TEAL = '#0F766E';

export default function AdminFlags() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [rows, setRows] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState('open'); // 'open' | 'resolved' | 'all'
  const [err, setErr] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const { rows } = await queryFlags({
        status: statusFilter === 'all' ? 'all' : statusFilter,
        limitN: 50,
      });
      setRows(rows);
    } catch (e) {
      const m = String(e?.message || e);
      if (m.includes('The query requires an index')) {
        setErr('This view needs a Firestore composite index. Open the link shown in the console to create it, then reload.');
        // eslint-disable-next-line no-console
        console.error(e);
      } else {
        setErr('Could not load flags.');
        // eslint-disable-next-line no-console
        console.error('[AdminFlags] load failed:', e);
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  React.useEffect(() => { load(); }, [load]);

  async function onResolve(flagId, note) {
    if (!currentUser?.uid) return;
    try {
      await resolveFlag(flagId, {
        newStatus: 'resolved',
        resolutionNote: note || null,
        resolvedByUid: currentUser.uid,
      });
      setRows((prev) =>
        prev.map((r) =>
          r.id === flagId ? { ...r, status: 'resolved', resolutionNote: note || null, resolvedByUid: currentUser.uid } : r
        )
      );
      toast.success('Flag resolved.');
    } catch (e) {
      const c = e?.code || '';
      if (c === 'permission-denied') {
        setErr('Failed to update flag: Missing or insufficient permissions. (Check Firestore rules for /flags update.)');
      } else {
        setErr(`Failed to update flag: ${e?.message || e}`);
      }
      toast.error('Failed to resolve flag.');
    }
  }

  async function onReopen(flagId) {
    if (!currentUser?.uid) return;
    try {
      await resolveFlag(flagId, {
        newStatus: 'open',
        resolutionNote: null,
        resolvedByUid: currentUser.uid,
      });
      setRows((prev) =>
        prev.map((r) =>
          r.id === flagId ? { ...r, status: 'open', resolutionNote: null, resolvedByUid: currentUser.uid } : r
        )
      );
      toast.success('Flag reopened.');
    } catch (e) {
      const c = e?.code || '';
      if (c === 'permission-denied') {
        setErr('Failed to update flag: Missing or insufficient permissions. (Check Firestore rules for /flags update.)');
      } else {
        setErr(`Failed to update flag: ${e?.message || e}`);
      }
      toast.error('Failed to reopen flag.');
    }
  }

  return (
    <>
      <h1 style={{ margin: '0 0 12px', fontFamily: 'serif', fontSize: 26, color: IVORY }}>Flags</h1>

      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <label htmlFor="flag-filter" style={{ color: IVORY, alignSelf: 'center' }}>Status:</label>
        <select
          id="flag-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={select}
          aria-label="Filter flags by status"
        >
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
          <option value="all">All</option>
        </select>
        <button style={btnMinor} onClick={load} disabled={loading} title="Reload">
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {err ? <div style={errBox}>{err}</div> : null}

      <div style={listBox}>
        <div style={thead}>
          <div>Created</div>
          <div>Memory</div>
          <div>Reason</div>
          <div>Reporter</div>
          <div>Owner</div>
          <div>Status</div>
          <div>Action</div>
        </div>

        {loading ? (
          <div style={{ color: WARM_GRAY, padding: 10 }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ color: WARM_GRAY, padding: 10 }}>No flags.</div>
        ) : (
          rows.map((f) => (
            <Row
              key={f.id}
              f={f}
              onResolve={onResolve}
              onReopen={onReopen}
            />
          ))
        )}
      </div>
    </>
  );
}

function Row({ f, onResolve, onReopen }) {
  const [note, setNote] = React.useState(f?.resolutionNote || '');

  const created =
    f?.createdAt?.toDate ? f.createdAt.toDate().toLocaleString() : '—';
  const memId = f.memoryId || '';

  return (
    <div style={trow}>
      <div title={created}>{created}</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <code style={{ fontSize: 12 }}>{memId || '—'}</code>
        {memId ? (
          <Link to={`/m/${memId}`} target="_blank" rel="noopener noreferrer" style={openLink}>
            Open ↗
          </Link>
        ) : null}
      </div>

      <div>{f.reason || '—'}</div>
      <code style={{ fontSize: 12 }}>{f.reporterUid || '—'}</code>
      <code style={{ fontSize: 12 }}>{f.ownerUid || '—'}</code>

      <div>
        <StatusPill status={f.status} />
        {f.status === 'resolved' && f.resolutionNote ? (
          <div style={{ color: WARM_GRAY, fontSize: 12, marginTop: 4 }} title={f.resolutionNote}>
            Note: {f.resolutionNote}
          </div>
        ) : null}
      </div>

      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {f.status === 'open' ? (
          <>
            <input
              placeholder="Resolution note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={input}
              aria-label="Resolution note"
            />
            <button style={btnTiny} onClick={() => onResolve(f.id, note)}>Resolve</button>
          </>
        ) : (
          <button style={btnTiny} onClick={() => onReopen(f.id)}>Reopen</button>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const ok = String(status) === 'resolved';
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 999,
      border: `1px solid ${ok ? 'rgba(15,118,110,0.42)' : GOLD}`,
      background: ok ? 'rgba(15,118,110,0.10)' : 'rgba(201,164,76,0.12)',
      color: ok ? TEAL : ACCESSIBLE_GOLD,
      fontWeight: 800,
      fontSize: 12
    }}>
      {ok ? 'resolved' : 'open'}
    </span>
  );
}

/* styles */
const listBox = { display: 'grid', gap: 6 };
const thead = {
  display: 'grid',
  gridTemplateColumns: '1.4fr 1.6fr 1fr 1.2fr 1.2fr 0.9fr 1.6fr',
  gap: 8,
  fontWeight: 800,
  color: IVORY,
  background: 'rgba(253,252,249,0.06)',
  padding: 10,
  borderRadius: 10,
};
const trow = {
  display: 'grid',
  gridTemplateColumns: '1.4fr 1.6fr 1fr 1.2fr 1.2fr 0.9fr 1.6fr',
  gap: 8,
  background: '#fff',
  color: NAVY,
  padding: 10,
  borderRadius: 10,
  alignItems: 'center',
};
const input = {
  flex: 1,
  padding: 8,
  borderRadius: 10,
  border: '1px solid rgba(10,26,51,0.2)',
  background: '#fff',
  minWidth: 160,
};
const btnTiny = {
  appearance: 'none',
  background: 'transparent',
  color: NAVY,
  border: `1px solid ${GOLD}`,
  padding: '4px 8px',
  borderRadius: 8,
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: 12,
};
const btnMinor = {
  appearance: 'none',
  background: 'transparent',
  color: IVORY,
  border: `1px solid ${GOLD}`,
  padding: '6px 10px',
  borderRadius: 10,
  cursor: 'pointer',
  fontWeight: 700,
};
const select = {
  appearance: 'none',
  background: '#fff',
  color: NAVY,
  border: `1px solid ${GOLD}`,
  borderRadius: 10,
  padding: '6px 10px',
  fontWeight: 700,
};
const openLink = {
  color: GOLD,
  textDecoration: 'none',
  fontWeight: 800,
  fontSize: 12,
};
const errBox = {
  color: BURGUNDY,
  background: 'rgba(92,26,43,0.08)',
  border: '1px dashed rgba(183,110,121,0.55)',
  padding: 10,
  borderRadius: 10,
  margin: '8px 0',
};
// ==========================
// End Version 3 — pages/AdminFlags.js
// ==========================
